/**
 * useDomainAvailability — FE-W-05.
 *
 * 입력 중 debounce + abort + LRU cache.
 *
 * 정책:
 *   - debounce 400ms (입력 5문자/초 시 마지막만 fetch)
 *   - AbortController — 진행 중 입력 변경 시 stale request 취소
 *   - LRU cache max 20 — 같은 도메인 재입력 시 fetch 0
 *   - 도메인 길이 < 4 → idle (fetch 안 함)
 *
 * 반환 status:
 *   - 'idle'        — 입력 없음 또는 너무 짧음
 *   - 'checking'    — fetch 진행 중 (debounce 통과 후)
 *   - 'available'   — 사용 가능
 *   - 'duplicate'   — 이미 등록 (taken_active 또는 taken_pending)
 *   - 'public_mail' — 공용메일 도메인
 *   - 'invalid'     — 형식 위반
 *   - 'error'       — 네트워크/서버 에러
 */

import { useEffect, useState } from 'react';
import { checkDomainAvailable } from '../api/onboarding';

const DEFAULT_DEBOUNCE_MS = 400;
const CACHE_MAX = 20;
const MIN_LENGTH = 4;

export type DomainAvailabilityStatus =
  | 'idle'
  | 'checking'
  | 'available'
  | 'duplicate'
  | 'public_mail'
  | 'invalid'
  | 'error';

export interface UseDomainAvailabilityState {
  status: DomainAvailabilityStatus;
  message?: string;
}

// LRU cache (Map의 insertion order = LRU order)
const cache = new Map<string, UseDomainAvailabilityState>();

function cacheGet(key: string): UseDomainAvailabilityState | null {
  const v = cache.get(key);
  if (!v) return null;
  // MRU: re-insert
  cache.delete(key);
  cache.set(key, v);
  return v;
}

function cacheSet(key: string, val: UseDomainAvailabilityState): void {
  if (cache.has(key)) cache.delete(key);
  cache.set(key, val);
  if (cache.size > CACHE_MAX) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
}

export function _resetDomainAvailabilityCacheForTests(): void {
  cache.clear();
}

export function useDomainAvailability(
  domainRaw: string,
  opts?: { debounceMs?: number },
): UseDomainAvailabilityState {
  const domain = domainRaw.trim().toLowerCase();
  const debounceMs = opts?.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  const [state, setState] = useState<UseDomainAvailabilityState>(() =>
    domain.length < MIN_LENGTH ? { status: 'idle' } : cacheGet(domain) ?? { status: 'idle' },
  );

  useEffect(() => {
    if (domain.length < MIN_LENGTH) {
      setState({ status: 'idle' });
      return;
    }
    const cached = cacheGet(domain);
    if (cached) {
      setState(cached);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setState({ status: 'checking' });
      try {
        const res = await checkDomainAvailable(domain, controller.signal);
        let next: UseDomainAvailabilityState;
        if (res.available) {
          next = { status: 'available', message: res.message };
        } else {
          switch (res.reason) {
            case 'taken_active':
            case 'taken_pending':
              next = { status: 'duplicate', message: res.message };
              break;
            case 'reserved_keyword':
              next = { status: 'public_mail', message: res.message };
              break;
            case 'invalid_format':
              next = { status: 'invalid', message: res.message };
              break;
            default:
              next = { status: 'error', message: '도메인 확인 실패' };
          }
        }
        cacheSet(domain, next);
        if (!controller.signal.aborted) setState(next);
      } catch (err) {
        if (controller.signal.aborted) return;
        const aborted =
          err instanceof Error &&
          (err.name === 'CanceledError' || err.name === 'AbortError');
        if (aborted) return;
        setState({ status: 'error', message: '확인 실패. 잠시 후 다시 시도해 주세요.' });
      }
    }, debounceMs);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [domain, debounceMs]);

  return state;
}
